import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import Calendar from 'react-calendar';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { appointmentsAPI, availabilityAPI, blockedDatesAPI } from '../services/api';
import { generateConfirmationCode, generateManagementToken, formatTime, isDateAvailable, generateTimeSlots, isValidEmail, isValidPhone } from '../utils/helpers';
import { downloadPDF } from '../utils/pdfGenerator';
import { format, addDays, parse } from 'date-fns';

const { FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiCheck, FiDownload, FiVideo, FiPhoneCall, FiZap, FiShield, FiRefreshCw, FiPlay, FiX } = FiIcons;

const Header = () => (
  <header className="bg-white border-b border-gray-200">
    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between h-20">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-600 flex items-center justify-center rounded-xl">
              <SafeIcon icon={FiCalendar} className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="ml-4">
            <a href="/#" className="text-xl md:text-2xl font-bold text-gray-800">
              Ligon Road Innovations LLC
              <span className="text-blue-600"> Scheduling</span>
            </a>
          </div>
        </div>
        <div className="hidden md:flex items-center space-x-6 text-sm">
          <div className="flex items-center text-gray-600">
            <SafeIcon icon={FiMail} className="h-5 w-5 mr-2 text-gray-500" />
            <span>drlm@admlead.com</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-400 mr-2">•</span>
            <a 
              href="https://share.deftform.com/1vR6cs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              Ask a Question
            </a>
          </div>
        </div>
      </div>
    </nav>
  </header>
);

const HeroSection = () => (
  <div className="bg-gradient-to-br from-primary-50 via-white to-primary-50 border-b border-primary-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
        >
          Schedule Your Appointment
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }} 
          className="text-xl text-gray-600 max-w-3xl mx-auto mb-12"
        >
          Book a convenient time for your consultation, discovery call, or follow-up meeting. 
          Fast, easy, and professional scheduling in under a minute.
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.2 }} 
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <SafeIcon icon={FiZap} className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Scheduling</h3>
            <p className="text-gray-600 text-sm">
              Choose from available time slots and book instantly with immediate confirmation.
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.3 }} 
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">30-Minute Sessions</h3>
            <p className="text-gray-600 text-sm">
              Focused, efficient meetings designed to maximize value for your time.
            </p>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.4 }} 
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
              <SafeIcon icon={FiRefreshCw} className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Flexible Options</h3>
            <p className="text-gray-600 text-sm">
              Virtual, in-person, or phone meetings to suit your preference and needs.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  </div>
);

const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Schedule Your Appointment</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Watch this quick video to learn how easy it is to book your appointment with our scheduling system.
          </p>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
            <video
              className="w-full aspect-video"
              controls
              poster="https://cdn1.sharemyimage.com/smi/2025/11/09/Lmcalinstructions.fr.jpeg"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            >
              <source src="https://cdn1.sharemyimage.com/smi/2025/11/09/Lmcalinstructions.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 shadow-2xl transform transition-transform hover:scale-105">
                  <SafeIcon icon={FiPlay} className="text-4xl text-primary-600 ml-1" />
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Video duration: 37 seconds • Best viewed in fullscreen
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingInstructions = () => (
  <div className="bg-white border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Book Your Appointment</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Select your preferred appointment type, choose an available time slot, and provide your details to confirm your booking.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 max-w-3xl mx-auto">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">1</div>
          <span className="text-gray-700 font-medium">Choose Appointment Type</span>
        </div>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">2</div>
          <span className="text-gray-700 font-medium">Select Date & Time</span>
        </div>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">3</div>
          <span className="text-gray-700 font-medium">Confirm Booking</span>
        </div>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <footer className="bg-white border-t border-gray-200">
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 text-center text-gray-500">
      <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mb-4">
        <a href="/#/admin" className="text-sm hover:text-primary-600 transition-colors">Admin Portal</a>
        <a href="https://app.getterms.io/view/pjlO8/acceptable-use/en-us" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary-600 transition-colors">Acceptable Use Policy</a>
        <a href="https://app.getterms.io/view/pjlO8/app-privacy/en-us" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary-600 transition-colors">App Privacy Policy</a>
        <a href="https://app.getterms.io/view/pjlO8/cookies/en-us" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary-600 transition-colors">Cookie Policy</a>
        <a href="https://app.getterms.io/policy/pjlO8/privacy" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary-600 transition-colors">Comprehensive Privacy Policy</a>
        <a href="https://app.getterms.io/view/pjlO8/terms-of-service/en-us" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-primary-600 transition-colors">Terms of Service</a>
      </div>
      <p className="text-sm">&copy;{new Date().getFullYear()} Ligon Road Innovations LLC. All Rights Reserved.</p>
    </div>
  </footer>
);

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availabilitySettings, setAvailabilitySettings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [existingAppointments, setExistingAppointments] = useState([]);
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const [formData, setFormData] = useState({
    visitor_name: '',
    visitor_email: '',
    visitor_phone: '',
    appointment_type: '',
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setInitialLoading(true);
      console.log('🔄 Starting to load initial data...');

      // Load each API call separately for better debugging
      console.log('📅 Loading availability settings...');
      const availabilityResponse = await availabilityAPI.getAll();
      console.log('✅ Availability settings loaded:', availabilityResponse);
      setAvailabilitySettings(availabilityResponse.data || []);

      console.log('🚫 Loading blocked dates...');
      const blockedResponse = await blockedDatesAPI.getAll();
      console.log('✅ Blocked dates loaded:', blockedResponse);
      setBlockedDates(blockedResponse.data || []);

      console.log('📋 Loading existing appointments...');
      const appointmentsResponse = await appointmentsAPI.getAll({ limit: 100 });
      console.log('✅ Existing appointments loaded:', appointmentsResponse);
      setExistingAppointments(appointmentsResponse.data || []);

      // Removed success toast for cleaner production experience
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      
      // Set default availability as fallback
      console.log('🔄 Setting default availability as fallback...');
      const defaultAvailability = [
        { day_of_week: 1, start_time: '09:00', end_time: '17:00', is_available: 1 },
        { day_of_week: 2, start_time: '09:00', end_time: '17:00', is_available: 1 },
        { day_of_week: 3, start_time: '09:00', end_time: '17:00', is_available: 1 },
        { day_of_week: 4, start_time: '09:00', end_time: '17:00', is_available: 1 },
        { day_of_week: 5, start_time: '09:00', end_time: '17:00', is_available: 1 },
      ];
      setAvailabilitySettings(defaultAvailability);
      console.log('✅ Default availability set:', defaultAvailability);
    } finally {
      setInitialLoading(false);
      console.log('🏁 Initial data loading completed');
    }
  };

  const appointmentTypes = [
    { id: 'consultation', name: 'Consultation', description: 'Initial discussion about your needs and goals', icon: FiUser, color: 'blue' },
    { id: 'discovery', name: 'Discovery Call', description: 'Explore how we can work together', icon: FiZap, color: 'green' },
    { id: 'followup', name: 'Follow-up Meeting', description: 'Review progress and next steps', icon: FiRefreshCw, color: 'purple' },
    { id: 'strategy', name: 'Strategy Session', description: 'Deep dive into specific challenges', icon: FiShield, color: 'orange' }
  ];

  const handleDateChange = (date) => {
    console.log('📅 Date changed:', date);
    setSelectedDate(date);
    setSelectedTime('');
    const slots = generateTimeSlots(availabilitySettings, date, existingAppointments);
    console.log('🕐 Generated time slots:', slots);
    setAvailableSlots(slots);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.visitor_name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!isValidEmail(formData.visitor_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!isValidPhone(formData.visitor_phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const appointmentData = {
        ...formData,
        appointment_date: format(selectedDate, 'yyyy-MM-dd'),
        appointment_time: selectedTime,
        status: 'confirmed',
        confirmation_code: generateConfirmationCode(),
        management_token: generateManagementToken(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('📝 Creating appointment:', appointmentData);
      const result = await appointmentsAPI.create(appointmentData);
      console.log('✅ Appointment created:', result);

      if (result.status === 'success') {
        setCreatedAppointment({ ...appointmentData, id: result.id });
        setStep(4);
        // Removed success toast for cleaner production experience
      }
    } catch (error) {
      console.error('❌ Failed to book appointment:', error);
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetBooking = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime('');
    setSelectedType('');
    setFormData({
      visitor_name: '',
      visitor_email: '',
      visitor_phone: '',
      appointment_type: '',
      notes: ''
    });
    setCreatedAppointment(null);
  };

  const morningSlots = availableSlots.filter(time => parseInt(time.split(':')[0]) < 12);
  const afternoonSlots = availableSlots.filter(time => parseInt(time.split(':')[0]) >= 12);

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading scheduling system...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait while we load availability data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow">
        <HeroSection />
        <VideoSection />
        <BookingInstructions />
        <main className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="type" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Select Appointment Type</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {appointmentTypes.map((type, index) => (
                      <motion.button
                        key={type.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => {
                          setSelectedType(type.id);
                          setFormData(prev => ({ ...prev, appointment_type: type.name }));
                          setStep(2);
                        }}
                        className={`p-6 rounded-2xl border-2 transition-all text-left ${
                          selectedType === type.id
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 bg-${type.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <SafeIcon icon={type.icon} className={`h-6 w-6 text-${type.color}-600`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">{type.name}</h3>
                            <p className="text-gray-600 text-sm">{type.description}</p>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="scheduler" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Choose Date & Time</h2>
                        <p className="text-gray-600 mt-1">
                          Selected: <span className="font-semibold text-primary-600">
                            {appointmentTypes.find(t => t.id === selectedType)?.name}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="bg-white p-0 rounded-2xl">
                      <Calendar
                        onChange={handleDateChange}
                        value={selectedDate}
                        minDate={new Date()}
                        maxDate={addDays(new Date(), 90)}
                        tileDisabled={({ date }) => !isDateAvailable(date, blockedDates, [])}
                      />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Available Times for <span className="text-primary-600">
                        {selectedDate ? format(selectedDate, 'MMMM do') : '...'}
                      </span>
                    </h2>
                    <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
                      {selectedDate ? (
                        availableSlots.length > 0 ? (
                          <>
                            {morningSlots.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-gray-500 mb-3">Morning</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                  {morningSlots.map(time => (
                                    <button
                                      key={time}
                                      onClick={() => setSelectedTime(time)}
                                      className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                        selectedTime === time
                                          ? 'bg-primary-600 text-white'
                                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {formatTime(time)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {afternoonSlots.length > 0 && (
                              <div>
                                <h3 className="text-sm font-semibold text-gray-500 mb-3">Afternoon</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                  {afternoonSlots.map(time => (
                                    <button
                                      key={time}
                                      onClick={() => setSelectedTime(time)}
                                      className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                        selectedTime === time
                                          ? 'bg-primary-600 text-white'
                                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                                      }`}
                                    >
                                      {formatTime(time)}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-gray-500">No available times for this date.</p>
                        )
                      ) : (
                        <p className="text-gray-500">Select a date to see available times.</p>
                      )}
                    </div>
                    <div className="flex space-x-4 mt-8">
                      <button onClick={() => setStep(1)} className="btn-secondary flex-1">Back</button>
                      <button onClick={() => setStep(3)} disabled={!selectedTime} className="btn-primary flex-1">Next</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="details" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-800">Enter Your Information</h2>
                  <p className="text-gray-600 mt-2">Please provide your contact details to confirm.</p>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                  <div className="bg-primary-50 rounded-xl p-4 mb-8">
                    <div className="text-center">
                      <p className="font-semibold text-primary-800 mb-2">
                        {appointmentTypes.find(t => t.id === selectedType)?.name}
                      </p>
                      <p className="text-primary-700">
                        {format(selectedDate, 'PPPP')} at {formatTime(selectedTime)}
                      </p>
                    </div>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="visitor_name"
                          value={formData.visitor_name}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="visitor_email"
                          value={formData.visitor_email}
                          onChange={handleInputChange}
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="visitor_phone"
                        value={formData.visitor_phone}
                        onChange={handleInputChange}
                        required
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="4"
                        className="form-input"
                      />
                    </div>
                    <div className="flex items-center gap-4 pt-4">
                      <button type="button" onClick={() => setStep(2)} className="btn-secondary w-full">Back</button>
                      <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center">
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          'Confirm Appointment'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}

            {step === 4 && createdAppointment && (
              <motion.div key="confirmation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-2xl mx-auto text-center">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheck} className="text-3xl text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-3">Appointment Confirmed!</h2>
                  <p className="text-gray-600 mb-8">Your appointment has been successfully booked.</p>
                  <div className="bg-gray-50 rounded-xl p-6 text-left space-y-4 mb-8 border">
                    <div className="flex justify-between"><span className="text-gray-600">Type:</span><span className="font-medium">{createdAppointment.appointment_type}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Name:</span><span className="font-medium">{createdAppointment.visitor_name}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Date:</span><span className="font-medium">{format(parse(createdAppointment.appointment_date, 'yyyy-MM-dd', new Date()), 'PPPP')}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Time:</span><span className="font-medium">{formatTime(createdAppointment.appointment_time)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Confirmation:</span><span className="font-mono font-bold text-primary-600">{createdAppointment.confirmation_code}</span></div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-6 mb-8 border border-blue-200">
                    <h3 className="font-semibold text-blue-900 mb-4">Meeting Details</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start space-x-3">
                        <SafeIcon icon={FiVideo} className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-900">Virtual Meeting</p>
                          <p className="text-blue-700">https://meetn.com/drlionelmaten</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <SafeIcon icon={FiPhoneCall} className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-900">Phone Access</p>
                          <p className="text-blue-700">+1-859-710-9286 (PIN: 8178007)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button onClick={resetBooking} className="btn-primary">Book Another Appointment</button>
                    <button onClick={() => downloadPDF(createdAppointment)} className="btn-secondary flex items-center justify-center gap-2">
                      <SafeIcon icon={FiDownload} />
                      Download PDF
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default BookingPage;